import {
  FormControl,
  FormGroup,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';

import { chooserCurrencyAndAssetsValidator } from 'src/app/form-config/validators/cross-field-validators/chooser-currency-and-assets-validator';
import { countriesOfOfferJurisdictionsValidator } from 'src/app/form-config/validators/cross-field-validators/countries-of-offer-jurisdictions.validator';
import { greaterLessRemainderComboValidator3 } from 'src/app/form-config/validators/cross-field-validators/greater-less-remainder-combo-3';
import { issuePlusTenorEqualsMaturity } from 'src/app/form-config/validators/cross-field-validators/issue-plus-tenor-equals-maturity';
import { remainderZeroValidator } from 'src/app/form-config/validators/cross-field-validators/remainder-zero';
import { ScheduleFormUtils } from '../../utils/schedule.utils';
import {
  LegalEnum,
  PricingEnum,
  SalesEnum,
  ScheduleType,
  SummaryEnum,
} from '../enums/product-capture.enums';
import { ROLL_CONVENTION_ENUM } from '../enums/roll-convention.enum';
import { RollAs } from './roll-as.const';
import { creditStartDateFormatValidator } from 'src/app/form-config/validators/credit-start-date-format.validator';
import { dropbackInvestmentLevelsValidator } from 'src/app/form-config/validators/cross-field-validators/dropback-investment-levels.validator';

// NB do not default % values or large numbers directly in this form model
// as it can cause unexpected behaviour in edit mode with the ThousandSeparator pipe
export const PRODUCT_CAPTURE_FORM_CONST = {
  summary: new UntypedFormGroup(
    {
      lifecycleStatus: new UntypedFormControl(null),
      productType: new UntypedFormControl(null, [Validators.required]),
      productName: new UntypedFormControl(null, [Validators.required]),
      recordStatus: new UntypedFormControl('Draft', [Validators.required]),
      recordStatusReason: new UntypedFormControl(null),
      isin: new UntypedFormControl(null),
      currency: new UntypedFormControl(null, [Validators.required]),
      tenor: new UntypedFormControl(null),
      frequency: new UntypedFormControl(null),
      tradingCalendars: new UntypedFormControl([]),
      settlementCalendars: new UntypedFormControl([]),
      settlementRollConvention: new UntypedFormControl(
        ROLL_CONVENTION_ENUM.FOLLOWING
      ),
      tradingRollConvention: new UntypedFormControl(
        ROLL_CONVENTION_ENUM.FOLLOWING
      ),
      accrualRollConvention: new UntypedFormControl(
        ROLL_CONVENTION_ENUM.FOLLOWING
      ),
      performanceType: new UntypedFormControl(null),
      settlementType: new UntypedFormControl('CASH'),
      underlyingAssetsGroup: new UntypedFormGroup({
        underlyingAssets: new UntypedFormControl([]),
        rollAs: new UntypedFormControl(RollAs.TOGETHER),
      }),
      referenceEntitiesDisplay: new UntypedFormControl([]),
      referenceEntitiesGroup: new UntypedFormGroup({
        referenceEntities: new UntypedFormControl([]),
      }),
      keyDates: new UntypedFormGroup({
        issueDate: new UntypedFormGroup({
          priority: new UntypedFormControl(null),
          value: new UntypedFormControl(null),
          anchorDate: new UntypedFormControl(null),
          offset: new UntypedFormControl(null),
          calendar: new UntypedFormControl(null),
          rollConvention: new UntypedFormControl(null),
        }),
        interestStartDate: new UntypedFormGroup({
          priority: new UntypedFormControl(),
          value: new UntypedFormControl(),
          anchorDate: new UntypedFormControl(),
          offset: new UntypedFormControl(),
          calendar: new UntypedFormControl(),
          rollConvention: new UntypedFormControl(),
        }),
        interestEndDate: new UntypedFormGroup({
          priority: new UntypedFormControl(),
          value: new UntypedFormControl(),
          anchorDate: new UntypedFormControl(),
          offset: new UntypedFormControl(),
          calendar: new UntypedFormControl(),
          rollConvention: new UntypedFormControl(),
        }),
        redemptionDate: new UntypedFormGroup({
          priority: new UntypedFormControl(null),
          value: new UntypedFormControl(null),
          anchorDate: new UntypedFormControl(null),
          offset: new UntypedFormControl(null),
          calendar: new UntypedFormControl(null),
          rollConvention: new UntypedFormControl(null),
        }),
        initialObservationDate: new UntypedFormGroup({
          priority: new UntypedFormControl(null),
          value: new UntypedFormControl(null),
          anchorDate: new UntypedFormControl(null),
          offset: new UntypedFormControl(null),
          calendar: new UntypedFormControl(null),
          rollConvention: new UntypedFormControl(null),
        }),
        finalObservationDate: new UntypedFormGroup({
          priority: new UntypedFormControl(null),
          value: new UntypedFormControl(null),
          anchorDate: new UntypedFormControl(null),
          offset: new UntypedFormControl(null),
          calendar: new UntypedFormControl(null),
          rollConvention: new UntypedFormControl(null),
        }),
        tradeDate: new UntypedFormGroup({
          priority: new UntypedFormControl(null),
          value: new UntypedFormControl(null),
          anchorDate: new UntypedFormControl(null),
          offset: new UntypedFormControl(null),
          calendar: new UntypedFormControl(null),
          rollConvention: new UntypedFormControl(null),
        }),
        dtePmtUnadjFirstT: new UntypedFormGroup({
          priority: new UntypedFormControl(null),
          value: new UntypedFormControl(null),
          anchorDate: new UntypedFormControl(null),
          offset: new UntypedFormControl(null),
          calendar: new UntypedFormControl(null),
          rollConvention: new UntypedFormControl(null),
        }),
        dtePmtUnadjLast0: new UntypedFormGroup({
          priority: new UntypedFormControl(null),
          value: new UntypedFormControl(null),
          anchorDate: new UntypedFormControl(null),
          offset: new UntypedFormControl(null),
          calendar: new UntypedFormControl(null),
          rollConvention: new UntypedFormControl(null),
        }),
        productFinalisationDate: new UntypedFormGroup({
          priority: new UntypedFormControl(null),
          value: new UntypedFormControl(null),
          anchorDate: new UntypedFormControl(null),
          offset: new UntypedFormControl(null),
          calendar: new UntypedFormControl(null),
          rollConvention: new UntypedFormControl(null),
        }),
      }),
      schedule: new FormGroup({
        scheduleStrategy: new FormControl(null, []),
        [ScheduleType.COUPON]: ScheduleFormUtils.createDefaultScheduleForm(
          ScheduleType.COUPON
        ),
        [ScheduleType.CALLABILITY]: ScheduleFormUtils.createDefaultScheduleForm(
          ScheduleType.CALLABILITY
        ),
        [ScheduleType.PARTICIPATION]: new FormGroup({}), // The other properties will be handled in the ParticipationScheduleComponent
      }),
    },
    [
      issuePlusTenorEqualsMaturity(
        'issueDate',
        'tenor',
        'redemptionDate',
        true
      ),
      chooserCurrencyAndAssetsValidator(),
    ]
  ),
  payoff: new FormGroup({
    coupon: new FormGroup({
      couponType: new FormControl('NONE'),
      frequency: new FormControl(null),
      conversionOnRedemption: new FormControl(null),
      fixedRate: new FormControl(null),
      fixedPeriods: new FormControl(null),
      floatingPeriods: new FormControl(null),
      referenceRate: new FormControl(null),
      spread: new FormControl(null),
      cap: new FormControl(null),
      floor: new FormControl(null),
      dayCount: new FormControl(null),
      rateInterpolation: new FormControl(),
      memory: new FormControl(null),
      multiplier: new FormControl(null),
      rateReset: new FormControl(null),

      rateCalculationMethod: new FormControl<string>(null),
      rateResetSuspensionLength: new FormControl<number>(null),
      suspensionLength: new FormControl<number>(null), // Not the same as rateResetSuspensionLength, this one goes in the Coupon table where the other goes in the ProductReferenceRates table
      rateResetLookbackShift: new FormControl<string>(null),
      rateObservationPeriodShift: new FormControl<string>(null),
      rateObsPeriodShiftExtraCalendars: new FormControl<string[]>(null),
      rangeAccrualRate: new FormControl<string>(null),
      barrierBreak: new FormControl<string>(null),
      couponCalculationDays: new FormControl<string>(null),

      barrier: new FormGroup({
        direction: new FormControl(null),
        level: new FormControl(null), // couponBarrier -- (move and rename)
        obsFrequency: new FormControl(null), // obvsFrequency (move and rename)
        stepSize: new FormControl(null), // couponBarrierStepSize -- (move and rename)
        touchAndBreak: new FormControl(null), // touchBreak -- (move and rename)
        type: new FormControl(null), // couponBarrierType -- (move and rename)
        lowerLevel: new FormControl(null),
        upperLevel: new FormControl(null),
        lowerStepSize: new FormControl(null),
        upperStepSize: new FormControl(null),
      }),
      performanceCalculation: new UntypedFormControl(null),
      restrike: new UntypedFormControl(null),
      strike: new UntypedFormControl(null),
      leverage: new UntypedFormControl(null),
      digitalStrike: new UntypedFormControl(null),
      digitalAmount: new UntypedFormControl(null),

      conditionalCouponStrike: new UntypedFormControl(null),
      conditionalRate: new UntypedFormControl(null),
    }),

    participation: new FormGroup({
      upside: new FormGroup({
        type: new FormControl('NONE'),
        callStrike: new FormControl(null),
        callParticipation: new FormControl(null),
        callSpreadStrike: new FormControl(null),
        putStrike: new FormControl(null),
        putParticipation: new FormControl(null),
        putSpreadStrike: new FormControl(null),
        digitalAmount: new FormControl(null),
        barrier: new FormGroup({
          type: new FormControl('FLAT'),
          stepSize: new FormControl(null),
          direction: new FormControl(null),
          level: new FormControl(null),
          obsFrequency: new FormControl(null),
          touchAndBreak: new FormControl(null),
        }),
        rebateAmount: new FormControl(null),
        earlyTermination: new FormControl(null),
        rebateDate: new FormControl(null),
        rebateSettlementLag: new FormControl(null),
      }),
      downside: new FormGroup({
        downsideType: new FormControl('NONE'),
        strike: new FormControl(null),
        leverage: new FormControl(null),
        leverageOptions: new FormControl(null),
        floorStrike: new FormControl(null),
        barrier: new FormGroup({
          type: new FormControl('FLAT'),
          stepSize: new FormControl(null),
          direction: new FormControl(null),
          knockInBarrier: new FormControl(null),
          obsFrequency: new FormControl(null),
          touchAndBreak: new FormControl(null),
        }),
      }),
      base: new FormGroup(
        {
          type: new FormControl('NONE'),
          leverage: new FormControl(null),
          bonusStrike: new FormControl(null),
          bonusAmount: new FormControl(null),
          capped: new FormControl(null),
          globalCap: new FormControl(null),
          globalFloor: new FormControl(null),
          barrier: new FormGroup({
            type: new FormControl('FLAT'),
            stepSize: new FormControl(null),
            direction: new FormControl(null),
            level: new FormControl(null),
            obsFrequency: new FormControl(null),
            touchAndBreak: new FormControl(null),
            investmentLevelFixingType: new FormControl(null),
            levelsAndInvestments: new UntypedFormArray([]),
          }),
          bonusKnockOut: new FormControl(null),
          strike: new FormControl(null),
          upperParticipation: new FormControl(null),
          issueAtDiscount: new FormControl(null),
          initialLevelAsDenomination: new FormControl(null),
          initialCashAllocation: new FormControl(null),
          initialInvestmentAllocation: new FormControl(null),
        },
        [dropbackInvestmentLevelsValidator()]
      ),
    }),
    capitalBase: new UntypedFormGroup({
      capitalBaseType: new UntypedFormControl('NONE'),
      capitalBase: new UntypedFormControl(null),
    }),
    callability: new UntypedFormGroup({
      callEventType: new UntypedFormControl('NONE'),
      frequency: new UntypedFormControl(null),
      deferredPeriods: new UntypedFormControl('0'),
      lastPeriodCallable: new UntypedFormControl(true),
      amountType: new UntypedFormControl(null),
      amount: new UntypedFormControl(null),
      amountStepSize: new UntypedFormControl(null),
      barrier: new UntypedFormGroup({
        direction: new UntypedFormControl(null),
        level: new UntypedFormControl(null), // barrierLevel (move + rename)
        obsFrequency: new UntypedFormControl(null), //  obvsFrequency (move + rename)
        stepSize: new UntypedFormControl(null), //  barrierStepSize (move + rename)
        touchAndBreak: new UntypedFormControl(null), // touchBreak (move + rename)
        type: new UntypedFormControl(null), //  barrierType (move + rename)
      }),
    }),
  }),
  creditProvision: new UntypedFormGroup({
    // General Provisions
    creditPeriodStartDaysBeforeTradeDate: new UntypedFormControl(null, [
      creditStartDateFormatValidator(),
    ]),
    creditPeriodEndDate: new UntypedFormControl(null),
    lossAtMaturity: new UntypedFormControl(null),
    dcDeterminations: new UntypedFormControl(null),
    noticeOfPubliclyAvailableInformation: new UntypedFormControl(null),
    excludedObligations: new UntypedFormControl(null),
    excludedObligationsList: new UntypedFormControl([]),

    obligationSpecifiedCurrency: new UntypedFormControl(null),
    obligationDomesticCurrency: new UntypedFormControl(null),
    allGuarantees: new UntypedFormControl(null),
    creditEvents: new UntypedFormControl(null),
    creditEventGracePeriod: new UntypedFormControl(null),

    accruedInterest: new UntypedFormControl(null),
    fixedRecoveryAmount: new UntypedFormControl(null),

    cashSettlementQuotationMethod: new UntypedFormControl(null),
    cashSettlementValuationMethod: new UntypedFormControl(null),
    minimumQuotation: new UntypedFormControl(null),

    passThroughSecurities: new UntypedFormControl(null),
    fxPriceSource: new UntypedFormControl(null),
    fixingRateTime: new UntypedFormControl(null),
    additionalCreditBusinessCentre: new UntypedFormControl(null),
    additionalCreditBusinessCentreDetails: new UntypedFormControl(null), // forgotten from new design but needed

    creditContingentCallOptionSecurities: new UntypedFormControl(null),
    creditEventAccruedCouponSecurities: new UntypedFormControl(null),

    detailsRelatingToInstalmentNotes: new UntypedFormControl(null),
    extensionPeriodInterest: new UntypedFormControl(null),
    kidWithdrawalRightsDisclosure: new UntypedFormControl(null),

    maturityCapitalProtectedSecurities: new UntypedFormControl(null),
    underlyingLinkedRedemptionSecurities: new UntypedFormControl(null),
    unwindCosts: new UntypedFormControl(null),

    // Settlement Provisions
    physicalSettlementMatrix: new UntypedFormControl(null),
    settlementMethod: new UntypedFormControl(null),
    fallbackSettlementMethod: new UntypedFormControl(null),
    escrow: new UntypedFormControl(null),
    settlementProvisions: new UntypedFormArray([]),
    ntceSupplement: new UntypedFormControl(null),
    provisionsMonolineInsurer: new UntypedFormControl(null),
    supplementTo2014ISDACDD: new UntypedFormControl(null),
    suppUSMunicipals: new UntypedFormControl(null),
    supp2014SovereignNoAssetPkgDelivery: new UntypedFormControl(null),
    provisionsForArgentineRepublic: new UntypedFormControl(null),
    addnProvisionsCertainRussianEntities: new UntypedFormControl(null),
    addnProvisionsCertainVenezuelanEntities: new UntypedFormControl(null),
    financialReferenceEntityTerms: new UntypedFormControl(null),
    addnProvisionsHellenicRepublic: new UntypedFormControl(null),
    provisionsForLPN: new UntypedFormControl(null),
    provisionsForRepublicOfHungary: new UntypedFormControl(null),
    provisionsForRussianFederation: new UntypedFormControl(null),
    seniorNonPreferredRefObligations: new UntypedFormControl(null),
    subordinatedEuropeanInsuranceTerms: new UntypedFormControl(null),
    provisionsForSukukCorporate: new UntypedFormControl(null),
    otherTermsOrConditions: new UntypedFormControl(null),
  }),
  sales: new UntypedFormGroup({
    targetMarket: new UntypedFormGroup({
      summaryRiskIndicator: new UntypedFormControl(null),
    }),
    targetMarkets: new UntypedFormArray([]),
    feesGroup: new UntypedFormGroup({
      feesArr: new UntypedFormArray([]),
    }),
    esgThematics: new FormControl(null),
    salesRegion: new FormControl(null),
    [SalesEnum.secondaryMarketAndDataPublising]: new FormGroup({
      [SalesEnum.salesSecondaryMktRequired]: new UntypedFormControl(false),
      [SalesEnum.salesTelekursSetUp]: new FormControl(null),
    }),
    [SalesEnum.salesSecondaryMktQuotationType]: new UntypedFormControl(false),
    contactInformation: new UntypedFormArray([]),
  }),
  legalForm: new UntypedFormGroup({
    legal: new UntypedFormGroup(
      {
        legalFormDropdown: new FormControl(null),
        formOfSecurities: new FormControl('BEARER_PERMANENT'),
        issuerEntity: new FormControl(null),
        issuerEntityId: new FormControl(null),
        legalProgramme: new FormControl(null),
        depositTaker: new FormControl(null),
        wrapper: new FormControl(null),
        issuePriceLegal: new FormControl(null),
        tradingInUnits: new FormControl(false),
        numberOfUnits: new FormControl(null),
        notional: new FormControl(null),
        denominationLegal: new FormControl(null),
        minTradingUnits: new FormControl(null),
        minTradingAmountLegal: new FormControl(null),
        incrementalTradingAmount: new FormControl(),
        incrementalTradingUnits: new FormControl(),
        maxTradingUnits: new FormControl(null),
        maxTradingAmountLegal: new FormControl(null),
        maxIssueSizeNotional: new FormControl(null),
        maxIssueSizeUnits: new FormControl(null),
        governingLaws: new FormControl(null),
        listingVenue: new FormControl(null),
        eligibleLiabilityFormat: new FormControl(false),
        listingStatus: new FormControl('UNLISTED'),
        securedIssuance: new FormControl(false),
      },
      [
        remainderZeroValidator(
          LegalEnum.notional,
          LegalEnum.denominationLegal,
          'Notional should be a multiple of Denomination'
        ),
        greaterLessRemainderComboValidator3(
          LegalEnum.minTradingAmount,
          LegalEnum.maxTradingAmount,
          LegalEnum.denominationLegal
        ),
        greaterLessRemainderComboValidator3(
          LegalEnum.minTradingUnits,
          LegalEnum.maxTradingUnits
        ),
        remainderZeroValidator(
          LegalEnum.maxIssueSizeNotional,
          LegalEnum.denominationLegal,
          'Max. Issue Notional should be a multiple of Denomination'
        ),
      ]
    ),
    productIdentifiers: new UntypedFormGroup({
      productIdentifiersArr: new UntypedFormArray([]),
    }),
    countriesOfOffer: new UntypedFormGroup({
      countriesOfOfferArr: new UntypedFormArray(
        [],
        countriesOfOfferJurisdictionsValidator()
      ),
    }),
  }),
  agents: new UntypedFormGroup({
    agentsArr: new UntypedFormArray([]),
  }),
  [PricingEnum.PRICING]: new FormGroup({
    [PricingEnum.FAIR_VALUE]: new FormControl(null),
    [PricingEnum.ECONOMIC_VALUE]: new FormControl(null),
    [PricingEnum.REOFFER_PRICE]: new FormControl(null),
  }),
  // This form will be just for tracking purposes. Its values will not be sent to the backend.
  eventSchedules: new UntypedFormGroup({
    schedules: new UntypedFormControl(),
  }),
  notesAndTasks: new FormGroup({
    notes: new FormControl(''),
    tasks: new UntypedFormArray([]),
  }),
};

// Setting default values in the form model above works on initial
// load but once the form is reset / cleared the values are lost. We reset
// the defaults by passing this object into instances of the form.reset()
// function in create-proudct.component.ts
export const PRODUCT_CAPTURE_FORM_RESET_DEFAULTS = {
  summary: {
    recordStatus: 'Draft',
    settlementType: 'CASH',
    settlementRollConvention: ROLL_CONVENTION_ENUM.FOLLOWING,
    accrualRollConvention: ROLL_CONVENTION_ENUM.FOLLOWING,
    tradingRollConvention: ROLL_CONVENTION_ENUM.FOLLOWING,
    [SummaryEnum.underlyingAssetsGroup]: {
      [SummaryEnum.underlyingAssets]: [],
      [SummaryEnum.rollAs]: RollAs.TOGETHER,
    },
    [SummaryEnum.schedule]: {
      scheduleStrategy: null,
      [ScheduleType.COUPON]: ScheduleFormUtils.createDefaultScheduleForm(
        ScheduleType.COUPON
      ),
      [ScheduleType.CALLABILITY]: ScheduleFormUtils.createDefaultScheduleForm(
        ScheduleType.CALLABILITY
      ),
      [ScheduleType.PARTICIPATION]: new FormGroup({}),
    },
  },
  payoff: {
    coupon: { couponType: 'NONE' },
    participation: {
      upside: { type: 'NONE' },
      downside: { downsideType: 'NONE' },
      base: { type: 'NONE' },
    },
    capitalBase: {
      capitalBaseType: 'NONE',
    },
    callability: {
      callEventType: 'NONE',
      deferredPeriods: '0',
    },
  },
  legalForm: {
    legal: {
      formOfSecurities: 'BEARER_PERMANENT',
      listingStatus: 'UNLISTED',
      eligibleLiabilityFormat: false,
    },
  },
};
